import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  canPushAnalyticsToDataLayer,
  GTM_READY_EVENT,
  pushVirtualPageView,
  readStoredConsent,
} from 'config/analyticsConfig';
import { captureUtmFromLocation } from 'config/analyticsUtm';

/**
 * F1.2 — virtual page_view on SPA routes (incl. first paint after GTM + consent).
 * F1.4 — capture utm_* into sessionStorage.
 *
 * On app/localhost Google Tag must not fire (GTM trigger = LP hostname only);
 * first hit comes from this tracker via `virtual_page_view`.
 */
const AnalyticsRouteTracker = () => {
  const location = useLocation();
  const lastSentKeyRef = useRef<string | null>(null);
  const [gtmEpoch, setGtmEpoch] = useState(0);

  useEffect(() => {
    const onReady = () => setGtmEpoch(n => n + 1);
    window.addEventListener(GTM_READY_EVENT, onReady);
    if (window.__gtmLoaded) onReady();
    return () => window.removeEventListener(GTM_READY_EVENT, onReady);
  }, []);

  useEffect(() => {
    captureUtmFromLocation(location.search);

    if (!canPushAnalyticsToDataLayer()) return;
    if (!readStoredConsent()?.analytics) return;

    const key = `${location.pathname}${location.search}`;
    if (lastSentKeyRef.current === key) return;
    lastSentKeyRef.current = key;
    pushVirtualPageView(location.pathname, location.search);
  }, [location.pathname, location.search, gtmEpoch]);

  return null;
};

export default AnalyticsRouteTracker;
